import { Fragment, MouseEvent } from 'react';
import {
  ConfirmationModal,
  DeleteButton,
  Grid,
  PrimaryButton,
} from '@/components';
import { useDeleteUser, useEditUserFormContext, useUpdateUser } from '../hooks';
import { useEditUserId } from '../store';
import { toast } from 'react-toastify';
import { useBoolean } from 'usehooks-ts';

type ManageProfileActionsProps = {
  onClose: () => void;
};

export const ManageProfileActions = (props: ManageProfileActionsProps) => {
  const { value: shouldShowDeleteDialog, toggle: toggleDeleteDialog } =
    useBoolean();
  const { mutate: deleteUser, isPending: isPendingDelete } = useDeleteUser();
  const { mutate: updateUser, isPending: isPendingUpdate } = useUpdateUser();
  const { watch } = useEditUserFormContext();
  const { firstName, lastName, email } = watch();
  const id = useEditUserId();

  const handleDelete = () => {
    deleteUser(id, {
      onSuccess: () => {
        toast.success('User deleted.');
        props.onClose();
      },
      onError: () => {
        toast.error('Error soft deleting user!');
      },
    });
  };

  const handleUpdate = () => {
    updateUser(
      { userId: id, firstName: firstName, lastName: lastName, email: email },
      {
        onSuccess: () => {
          toast.success('User updated.');
          props.onClose();
        },
        onError: () => {
          toast.error('Error updating user!');
        },
      },
    );
  };

  return (
    <Fragment>
      <Grid columns={2} height={50} gap={20} mt={8}>
        <DeleteButton onClick={toggleDeleteDialog} disabled={isPendingDelete}>
          <svg
            width="19"
            height="18"
            viewBox="0 0 19 18"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M14 4.5L5 13.5M5 4.5L14 13.5"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          Delete User
        </DeleteButton>
        <PrimaryButton onClick={() => handleUpdate()}>
          <svg
            width="19"
            height="18"
            viewBox="0 0 19 18"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{ marginRight: 8 }}
          >
            <path
              d="M11.75 3.75004L14.7499 6.75004M16.3805 5.10904C16.777 4.71261 16.9998 4.17489 16.9999 3.61418C16.9999 3.05347 16.7773 2.5157 16.3808 2.11917C15.9844 1.72263 15.4467 1.49983 14.886 1.49976C14.3253 1.49969 13.7875 1.72236 13.391 2.11879L3.38145 12.1305C3.20732 12.3042 3.07854 12.5179 3.00645 12.753L2.0157 16.017C1.99632 16.0819 1.99485 16.1508 2.01146 16.2164C2.02807 16.2821 2.06214 16.342 2.11005 16.3898C2.15796 16.4376 2.21792 16.4716 2.28357 16.4881C2.34922 16.5046 2.41812 16.503 2.48295 16.4835L5.7477 15.4935C5.98258 15.4221 6.19633 15.2941 6.3702 15.1208L16.3805 5.10904Z"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          Update User
        </PrimaryButton>
      </Grid>

      <ConfirmationModal
        open={shouldShowDeleteDialog}
        isPending={isPendingDelete}
        onClose={toggleDeleteDialog}
        title="Delete user?"
        content="Are you sure you want to delete this user?"
        cancelActionTitle="Cancel"
        confirmActionTitle="Yes"
        onConfirm={() => handleDelete()}
        onCancel={toggleDeleteDialog}
      />
    </Fragment>
  );
};
